import { ref, markRaw } from 'vue';

export class FilterStatusHistoryContext {
  constructor(duckDBService) {
    markRaw(this);
    this.duckDBService = markRaw(duckDBService);
    this.filterQueries = markRaw({});     // Store the actual queries
    this.filters = markRaw({});           // Visual metadata
    this.filtersLastUpdated = ref(new Date());
  }

  async initializeFilterContext() {
    const conn = this.duckDBService.conn;

    await conn.query(`
      DROP VIEW  IF EXISTS filteredStatusHistory;
      DROP TABLE IF EXISTS status_history_filter_context;

      CREATE TEMPORARY TABLE status_history_filter_context(subItemId VARCHAR, validFrom TIMESTAMP, statusId VARCHAR);

      INSERT INTO status_history_filter_context
      SELECT DISTINCT subItemId, validFrom, statusId FROM status_history_temporal;

      CREATE VIEW filteredStatusHistory AS
      SELECT 
          sht.statusId AS statusId,
          sht.locationId,
          sht.profileId,
          sht.createdByTeamId AS createdByTeamId,
          sht.createdByUserId AS createdByUserId,
          sht.subItemId AS subItemId,
          sht.validFrom AS validFrom,
          sht.validTo AS validTo,
      FROM status_history_temporal sht
      INNER JOIN status_history_filter_context fc ON 
          sht.subItemId = fc.subItemId AND 
          sht.validFrom = fc.validFrom AND
          sht.statusId = fc.statusId
    `);

    this.filters = markRaw({});
    this.filterQueries = markRaw({});
    this.filtersLastUpdated.value = new Date();
  }

  // New method: Update filter directly from SQL
  async updateFilterWithQuery(visualId, query) {
    const conn = this.duckDBService.conn;
    
    try {
      // Register query as a view for this filter
      await conn.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS (
          SELECT DISTINCT subItemId, validFrom, statusId FROM (${query})
        );
      `);
      
      // Store the query reference
      this.filterQueries[visualId] = query;
      
      // Get count for metadata
      const countResult = await conn.query(`SELECT COUNT(*) as count FROM filter_view_${visualId}`);
      const count = countResult.toArray()[0].count;
      this.filters[visualId] = count;
      
      // Apply the combined filters
      await this.applyFiltersWithSQL();
      
    } catch (error) {
      console.error('Error updating filter with query:', error);
      // Clean up if needed
    }
  }

  // Apply all current filters using SQL intersection
  async applyFiltersWithSQL() {
    const conn = this.duckDBService.conn;
    const filterIds = Object.keys(this.filters);
    
    if (filterIds.length === 0) {
      await this.resetFilters();
      return;
    }
    
    try {
      // Use SQL INTERSECT to combine all filters
      if (filterIds.length === 1) {
        // Only one filter - simple replacement
        await conn.query(`
          DELETE FROM status_history_filter_context;
          INSERT INTO status_history_filter_context
          SELECT subItemId, validFrom, statusId FROM filter_view_${filterIds[0]};
        `);
      } else {
        // Multiple filters - use SQL INTERSECT
        const intersectionQuery = filterIds
          .map(id => `SELECT subItemId, validFrom, statusId FROM filter_view_${id}`)
          .join(' INTERSECT ');
        
        await conn.query(`
          DELETE FROM status_history_filter_context;
          INSERT INTO status_history_filter_context
          ${intersectionQuery};
        `);
      }
      
      this.filtersLastUpdated.value = new Date();
    } catch (error) {
      console.error('Error applying filters with SQL:', error);
      // Recover from error
      await this.resetFilters();
    }
  }

  // Legacy support for ID-based filtering - adapted for composite key
  async updateFilter(visualId, filteredRecords) {
    const conn = this.duckDBService.conn;
    
    // filteredRecords should be an array of objects with subItemId, validFrom, statusId
    // Store for tracking
    this.filters[visualId] = filteredRecords.length;
    
    if (filteredRecords.length === 0) {
      // Empty filter case
      await conn.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS 
        SELECT 
          CAST(NULL AS VARCHAR) AS subItemId,
          CAST(NULL AS TIMESTAMP) AS validFrom,
          CAST(NULL AS VARCHAR) AS statusId
        WHERE 1=0;
      `);
    } else {
      // Create a temporary table and view
      const tempTable = `temp_filter_${visualId}`;
      
      await conn.query(`DROP TABLE IF EXISTS ${tempTable}`);
      await conn.query(`CREATE TEMPORARY TABLE ${tempTable}(
        subItemId VARCHAR,
        validFrom TIMESTAMP,
        statusId VARCHAR
      )`);
      
      // Insert data in batches
      const batchSize = 200000;
      for (let i = 0; i < filteredRecords.length; i += batchSize) {
        const chunk = filteredRecords.slice(i, i + batchSize)
          .map(v => `(
            '${v.subItemId.replace(/'/g, "''")}', 
            '${v.validFrom.replace(/'/g, "''")}', 
            '${v.statusId.replace(/'/g, "''")}'
          )`).join(',');
        
        if (chunk) {
          await conn.query(`INSERT INTO ${tempTable} VALUES ${chunk}`);
        }
      }
      
      // Create a view from the table
      await conn.query(`
        DROP VIEW IF EXISTS filter_view_${visualId};
        CREATE TEMPORARY VIEW filter_view_${visualId} AS 
        SELECT DISTINCT subItemId, validFrom, statusId FROM ${tempTable};
      `);
      
      // Clean up temp table
      await conn.query(`DROP TABLE ${tempTable}`);
    }
    
    // Apply combined filters
    await this.applyFiltersWithSQL();
  }

  async removeFilter(visualId) {
    if (!this.filters[visualId]) return;
    
    const conn = this.duckDBService.conn;
    
    // Remove filter view
    await conn.query(`DROP VIEW IF EXISTS filter_view_${visualId}`);
    
    // Remove from tracking
    delete this.filters[visualId];
    delete this.filterQueries[visualId];
    
    if (Object.keys(this.filters).length === 0) {
      await this.resetFilters();
    } else {
      await this.applyFiltersWithSQL();
    }
  }

  async resetFilters() {
    const conn = this.duckDBService.conn;
    
    await conn.query(`
      DELETE FROM status_history_filter_context;
      INSERT INTO status_history_filter_context
      SELECT DISTINCT subItemId, validFrom, statusId FROM status_history_temporal;
    `);
    
    this.filters = markRaw({});
    this.filterQueries = markRaw({});
    this.filtersLastUpdated.value = new Date();
  }

  async getFilteredRecords() {
    const conn = this.duckDBService.conn;
    try {
      const res = await conn.query('SELECT subItemId, validFrom, statusId FROM status_history_filter_context');
      if (res?.getColumn) {
        const subItemIds = res.getColumn('subItemId').toArray();
        const validFroms = res.getColumn('validFrom').toArray();
        const statusIds = res.getColumn('statusId').toArray();
        
        return subItemIds.map((subItemId, i) => ({
          subItemId,
          validFrom: validFroms[i],
          statusId: statusIds[i]
        }));
      }
      if (Array.isArray(res)) return res.map(r => ({
        subItemId: r.subItemId,
        validFrom: r.validFrom,
        statusId: r.statusId
      }));
      if (res?.data) return res.data.map(r => ({
        subItemId: r.subItemId,
        validFrom: r.validFrom,
        statusId: r.statusId
      }));
      return [];
    } catch (error) {
      console.error('Error getting filtered records:', error);
      return [];
    }
  }

  // Adjusted executeFilteredQuery method for status_history_temporal
  async executeFilteredQuery(sql) {
    const conn = this.duckDBService.conn;
    const ts = this.filtersLastUpdated.value.getTime();
    try {
      return await conn.query(`
        SELECT q.*
        FROM (${sql}) AS q
        INNER JOIN filter_context fc ON 
          q.subItemId = fc.subItemId AND
          q.validFrom = fc.validFrom AND
          q.statusId = fc.statusId
        /* ${ts} */
      `);
    } catch (error) {
      console.error('Error executing filtered query:', error);
      throw error; 
    }
  }

  getFilterDebugInfo() {
    return {
      activeFilters: Object.keys(this.filters).length,
      filtersByVisual: Object.entries(this.filters).map(([vid, count]) => ({
        visualId: vid,
        matchCount: count,
        hasQuery: !!this.filterQueries[vid]
      })),
      lastUpdated: this.filtersLastUpdated.value
    };
  }
}